import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";
import { jwtDecode } from "jwt-decode";
import * as services from "./../services/saleng";
import {
  getAdminBranchId,
  getAdminRole,
  isBranchScopedAdminRole,
  isSuperAdminOnlyRole,
  isSupportedAdminRole,
} from "./adminAccess";
import logo from "../images/scrapyard.svg";

// Update schema to use username instead of email
const schema = yup.object().shape({
  username: yup.string().required("Please enter username"),
  password: yup
    .string()
    .min(8, "Password must be at least 8 characters")
    .max(20, "Password must not exceed 20 characters")
    .notOneOf(
      [yup.ref("username"), null],
      "Password must not be the same as your username",
    )
    .required("Please enter password"),
});

export default function Login() {
  const navigate = useNavigate();
  const clearAuthSession = () => {
    localStorage.removeItem("accessTokenGreen");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("shopName");
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const onSubmitNew = async (data) => {
    const payload = {
      username: data.username,
      password: data.password,
      app: "ADMIN",
    };

    try {
      // Use the same local API login as POS. This keeps the existing User,
      // password hash, role, shop, and branch data as the source of truth.
      const loginResponse = await services.loginShop(payload);
      const loginData = loginResponse.data;

      if (!loginData.success) {
        toast.error(loginData.error || "Something went wrong");
        return;
      }

      const { accessToken } = loginData.data || {};
      if (!accessToken) {
        throw new Error("Login response did not include an access token");
      }

      // Keep the existing Admin storage key so the rest of the app can use
      // the new local-API token without a second migration.
      localStorage.setItem("accessToken", accessToken);

      const decoded = jwtDecode(accessToken);
      const role = getAdminRole(decoded);

      // Admin App is a system-level account and does not need a shopId.
      // This check must happen before getShop(), otherwise the request becomes
      // /shop/null and fails Elysia's numeric id validation.
      if (isSuperAdminOnlyRole(role)) {
        if (!isSupportedAdminRole(role)) {
          clearAuthSession();
          await Swal.fire({
            icon: "error",
            title: "คุณไม่มีสิทธิ์เข้าถึง",
            confirmButtonText: "ตกลง",
            confirmButtonColor: "#185519",
          });
          return;
        }

        navigate("/super-admin");
        return;
      }

      // Fetch the Shop linked to the logged-in User.
      const shopInfoResponse = await services.getShop();
      const shopInfoData = shopInfoResponse.data.data;

      if (!shopInfoData) {
        throw new Error("ไม่พบร้านค้าที่ผูกกับผู้ใช้งานนี้");
      }

      const ownBranch = isBranchScopedAdminRole(role)
        ? shopInfoData?.branches?.find(
            (branch) => Number(branch.id) === getAdminBranchId(decoded),
          )
        : null;
      const hasAccess =
        isSupportedAdminRole(role) &&
        (!isBranchScopedAdminRole(role) ||
          (ownBranch?.priceAnnouncementType === "SELF_ANNOUNCE" &&
            ownBranch?.usePriceInsurance !== true));

      if (!hasAccess) {
        clearAuthSession();
        await Swal.fire({
          icon: "error",
          title: "คุณไม่มีสิทธิ์เข้าถึง",
          confirmButtonText: "ตกลง",
          confirmButtonColor: "#185519",
        });
        return;
      }

      localStorage.setItem("shopName", shopInfoData.name);
      navigate("/products");
    } catch (error) {
      clearAuthSession();
      if (error?.response?.status === 403) {
        await Swal.fire({
          icon: "error",
          title: "คุณไม่มีสิทธิ์เข้าถึง",
          confirmButtonText: "ตกลง",
          confirmButtonColor: "#185519",
        });
        return;
      }
      toast.error(
        error?.response?.data?.error || error?.message || "Something went wrong",
      );
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-custom-gray">
      <Toaster />
      <form
        onSubmit={handleSubmit(onSubmitNew)}
        className="w-full max-w-md p-8 space-y-4 bg-white shadow-md rounded-2xl"
      >
        <div className="flex items-center justify-center mb-6">
          <img
            src={logo}
            alt="logo"
            style={{ width: "350px", height: "auto" }}
          />
        </div>
        <h2 className="text-2xl font-bold mb-7 text-center mt-3 text-blue-900">
          Admin
        </h2>
        <div>
          <h2 className="text-2xl font-bold mb-7 text-center mt-3 text-blue-900">
            เข้าสู่ระบบ
          </h2>

          <div className="mt-5">
            <label htmlFor="username" className="text-sm font-semibold mb-1">
              ชื่อผู้ใช้
            </label>
            <input
              className="w-full px-3 py-2 border rounded"
              type="text"
              placeholder="ชื่อผู้ใช้"
              {...register("username")}
            />
            {errors?.username && (
              <p className="text-red-500 text-xs mt-1">
                {errors.username?.message}
              </p>
            )}
          </div>

          <div className="mt-5">
            <label htmlFor="password" className="text-sm font-semibold mb-1">
              รหัสผ่าน
            </label>
            <input
              className="w-full px-3 py-2 border rounded"
              type="password"
              placeholder="รหัสผ่าน" //l33iisu0
              {...register("password")}
            />
            {errors?.password && (
              <p className="text-red-500 text-xs mt-1">
                {errors.password?.message}
              </p>
            )}
          </div>

          <div className="mt-8">
            <button
              className="w-full px-3 py-2 text-white rounded-md hover:opacity-90"
              type="submit"
              style={{ backgroundColor: "#185519" }}
            >
              <span className="text-sm">เข้าสู่ระบบ</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
